import {useContext, useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {STATUS_COLORS} from "../../../config/statusColors";
import {
    Box, Stack, Typography, Card, Button, IconButton, Chip, Menu, MenuItem,
    Divider, TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress, Tabs, Tab,
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
    AlertCircleIcon, ArrowLeft01Icon, CheckIcon, Clock01Icon,
    Delete02Icon, File02Icon, Layers01Icon, Message01Icon,
    MoreVerticalIcon, NetworkIcon, Refresh01Icon, Search01Icon, ServerIcon,
    Settings01Icon, SdCardIcon, Upload01Icon,
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {Context} from "../../../context/AppContext";
import {KnowledgeManagerLayout} from "./components/KnowledgeManagerLayout";
import {DocumentUpload} from "./components/DocumentUpload";
import {DocumentPreview} from "./components/DocumentPreview";
import {QueryInterface} from "./components/QueryInterface";
import {KnowledgeGraphView} from "./components/KnowledgeGraphView";
import {ServerConfig} from "./components/ServerConfig";
import {KnowledgeDatabaseRepository} from "../../../repositories/KnowledgeDatabaseRepository";
import {useStompWebsocketRag} from '../../../hooks/useStompWebsocketRag'

const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};


const formatDate = (iso, withTime) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        ...(withTime ? {hour: "2-digit", minute: "2-digit"} : {}),
    });
};

const STATUS_PRIORITY = {processing: 0, indexed: 1, failed: 1}
const statusColor = (status) => {
    if (status === "online" || status === "indexed") return {bg: STATUS_COLORS.success.bg, color: STATUS_COLORS.success.text};
    if (status === "connecting" || status === "processing") return {bg: STATUS_COLORS.warning.bg, color: STATUS_COLORS.warning.text};
    if (status === "failed") return {bg: STATUS_COLORS.error.bg, color: STATUS_COLORS.error.text};
    return {bg: STATUS_COLORS.info.bg, color: STATUS_COLORS.info.text};
};

export const KnowledgeManagerTopicDetailsPage = ({project}) => {
    const {projectId, topicId} = useParams();
    const navigate = useNavigate();
    const {
        state,
        loadKbWorkspace,
        deleteKbDocument,
        refreshKbTopic,
        getAllKbTopics
    } = useContext(Context);
    const [liveDocs, setLiveDocs] = useState([]);
    const workspace = state.kbWorkspace[topicId] ?? {};
    const topic = workspace.topic ?? null;
    const documents = workspace.documents ?? [];

    const [activeTab, setActiveTab] = useState("documents");
    const [searchDocs, setSearchDocs] = useState("");
    const [uploadOpen, setUploadOpen] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [deleteDocId, setDeleteDocId] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [isPinging, setIsPinging] = useState(false);

    useEffect(() => {
        if (state.kbWorkspace[topicId]) return;
        loadKbWorkspace(topicId);
    }, [topicId]);
    useEffect(() => {
        setLiveDocs(documents);
    }, [workspace.documents]);

    const [updatedValue] = useStompWebsocketRag(topic ? `/topic/topics/${topic.id}/documents` : null);

    useEffect(() => {
        if (!updatedValue || String(updatedValue.topicId) !== String(topicId)) return;

        setLiveDocs(prev => {
            if (updatedValue.event === "deleted") {
                return prev.filter(d => d.id !== updatedValue.document.id);
            }
            const exists = prev.some(d => d.id === updatedValue.document.id);
            if (exists) {
                return prev.map(d => {
                    if (d.id !== updatedValue.document.id) return d;
                    const currentPriority = STATUS_PRIORITY[d.status] ?? -1;
                    const incomingPriority = STATUS_PRIORITY[updatedValue.document.status] ?? -1;
                    if (incomingPriority < currentPriority) return d;
                    return {...d, ...updatedValue.document};
                });
            }
            return [...prev, updatedValue.document];
        });
    }, [updatedValue, topicId]);
    if (!workspace.topic && !state.kbWorkspace[topicId]) {
        return (
            <KnowledgeManagerLayout>
                <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300}}>
                    <CircularProgress size={28}/>
                </Box>
            </KnowledgeManagerLayout>
        );
    }

    if (!topic) {
        return (
            <KnowledgeManagerLayout>
                <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300}}>
                    <Box sx={{textAlign: "center"}}>
                        <HugeiconsIcon icon={AlertCircleIcon} size={40} color={"#9ca3af"} style={{marginBottom: 8}}/>
                        <Typography fontWeight={600}>Topic Not Found</Typography>
                        <Typography fontSize={13} color={"text.secondary"} sx={{mt: 0.5}}>
                            The topic you're looking for doesn't exist.
                        </Typography>
                        <Button
                            variant={"modal"}
                            sx={{
                                mt: 2,
                                bgcolor: "#000",
                                color: "#fff",
                                textTransform: "none",
                                boxShadow: "none",
                                "&:hover": {bgcolor: "#000"}
                            }}
                            onClick={() => navigate(`/project/${projectId}/knowledge-manager`)}
                        >
                            Back to Dashboard
                        </Button>
                    </Box>
                </Box>
            </KnowledgeManagerLayout>
        );
    }

    const filteredDocs = liveDocs.filter(d => d.name.toLowerCase().includes(searchDocs.toLowerCase()));
    const serverStatus = topic.server?.status ?? "offline";
    const serverStyle = statusColor(serverStatus);

    const handlePing = async () => {
        setIsPinging(true);
        try {
            await refreshKbTopic(topic.id);
            toast.success("Health check complete");
        } catch (err) {
            toast.error("Health check failed");
        } finally {
            setIsPinging(false);
        }
    };

    const handleDeleteDoc = async () => {
        if (!deleteDocId) return;
        setIsDeleting(true);
        try {
            await deleteKbDocument(topic.id, deleteDocId, liveDocs);
            setDeleteDocId(null)
            setDeleteDialogOpen(false);
        } catch {

        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteTopic = async () => {
        setIsDeleting(true);
        try {
            await KnowledgeDatabaseRepository.deleteTopic(topic.id);
            toast.success("Topic deleted");
            getAllKbTopics(project?.settings?.organization);
            navigate(`/project/${projectId}/knowledge-manager`);
        } catch {
            toast.error("Failed to delete topic");
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <KnowledgeManagerLayout>
            <Box sx={{flex: 1, overflow: "auto"}}>
                <Box sx={{p: 3, maxWidth: 1100, mx: "auto"}}>

                    <Stack direction={"row"} alignItems={"flex-start"} justifyContent={"space-between"} gap={2}
                           sx={{mb: 3}}>
                        <Stack direction={"row"} alignItems={"flex-start"} gap={1.5}>
                            <IconButton onClick={() => navigate(`/project/${projectId}/knowledge-manager`)}
                                        sx={{mt: 0.25}}>
                                <HugeiconsIcon icon={ArrowLeft01Icon} size={18}/>
                            </IconButton>
                            <Box>
                                <Stack direction={"row"} alignItems={"center"} gap={1.25}>
                                    <Box sx={{width: 14, height: 14, borderRadius: "50%", bgcolor: topic.color, flexShrink: 0}} />
                                    <Typography variant={"h5"} fontWeight={700}>{topic.name}</Typography>
                                    <Chip
                                        icon={
                                            serverStatus === "online" ? <HugeiconsIcon icon={CheckIcon} size={12}/>
                                                : serverStatus === "connecting" ? <CircularProgress size={10}/>
                                                    : <HugeiconsIcon icon={AlertCircleIcon} size={12}/>
                                        }
                                        label={serverStatus}
                                        size={"small"}
                                        sx={{
                                            fontSize: 10,
                                            height: 20,
                                            bgcolor: serverStyle.bg,
                                            color: serverStyle.color
                                        }}
                                    />
                                </Stack>
                                <Typography fontSize={13} color={"text.secondary"} sx={{mt: 0.5}}>
                                    {topic.description}
                                </Typography>
                                <Stack direction={"row"} gap={2} sx={{mt: 1, fontSize: 11, color: "text.secondary"}}>
                                    {topic.server && (
                                        <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                        <HugeiconsIcon icon={ServerIcon} size={12}/> {topic.server.host}:{topic.server.port}
                                        </Stack>
                                    )}
                                    <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                        <HugeiconsIcon icon={File02Icon} size={12}/> {topic.documentCount} documents
                                    </Stack>
                                    <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                        <HugeiconsIcon icon={Message01Icon} size={12}/> {topic.queryCount} queries
                                    </Stack>
                                    <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                        <HugeiconsIcon icon={Clock01Icon} size={12}/> Updated {formatDate(topic.updatedAt)}
                                    </Stack>
                                </Stack>
                            </Box>
                        </Stack>

                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                            {topic.server && (
                                <Button
                                    variant={"ghost"} size={"small"}
                                    startIcon={isPinging ? <CircularProgress size={12}/> : <HugeiconsIcon icon={Refresh01Icon} size={14}/>}
                                    onClick={handlePing}
                                    disabled={isPinging}
                                    sx={{
                                        textTransform: "none",
                                        fontSize: 12,
                                        borderRadius: 2,
                                        borderColor: "divider",
                                        color: "text.primary",
                                        "&:hover": {
                                            borderColor: "divider",
                                            bgcolor: "action.hover",
                                        },
                                        "&:focus, &:focus-visible": {
                                            borderColor: "divider",
                                            outline: "none",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    Ping
                                </Button>
                            )}
                            <IconButton
                                onClick={(e) => setMenuAnchor(e.currentTarget)}
                                sx={{border: "1px solid", borderColor: "divider", borderRadius: 2}}
                            >
                                <HugeiconsIcon icon={MoreVerticalIcon} size={16}/>
                            </IconButton>
                            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                                <MenuItem onClick={() => {
                                    setActiveTab("settings");
                                    setMenuAnchor(null);
                                }}>
                                    <HugeiconsIcon icon={Settings01Icon} size={15} style={{marginRight: 8}}/> Settings
                                </MenuItem>
                                <Divider/>
                                <MenuItem
                                    onClick={() => {
                                        setDeleteDocId(null);
                                        setDeleteDialogOpen(true);
                                        setMenuAnchor(null);
                                    }}
                                    sx={{color: "error.main"}}
                                >
                                    <HugeiconsIcon icon={Delete02Icon} size={15} style={{marginRight: 8}}/> Delete Topic
                                </MenuItem>
                            </Menu>
                        </Stack>
                    </Stack>

                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{
                            minHeight: 36, mb: 2.5,
                            borderBottom: "1px solid", borderColor: "divider",
                            "& .MuiTabs-indicator": {bgcolor: "#000"},
                        }}
                    >
                        <Tab value={"documents"} label={"Documents"} icon={<HugeiconsIcon icon={File02Icon} size={14}/>}
                             iconPosition={"start"}
                             sx={{
                                 minHeight: 36,
                                 textTransform: "none",
                                 fontSize: 12.5,
                                 "&.Mui-selected": {color: "#000"}
                             }}/>
                        <Tab value={"query"} label={"Query"} icon={<HugeiconsIcon icon={Search01Icon} size={14}/>} iconPosition={"start"}
                             sx={{
                                 minHeight: 36,
                                 textTransform: "none",
                                 fontSize: 12.5,
                                 "&.Mui-selected": {color: "#000"}
                             }}/>
                        <Tab value={"graph"} label={"Knowledge Graph"} icon={<HugeiconsIcon icon={NetworkIcon} size={14}/>}
                             iconPosition={"start"}
                             sx={{
                                 minHeight: 36,
                                 textTransform: "none",
                                 fontSize: 12.5,
                                 "&.Mui-selected": {color: "#000"}
                             }}/>
                        <Tab value={"settings"} label={"Settings"} icon={<HugeiconsIcon icon={Settings01Icon} size={14}/>}
                             iconPosition={"start"}
                             sx={{
                                 minHeight: 36,
                                 textTransform: "none",
                                 fontSize: 12.5,
                                 "&.Mui-selected": {color: "#000"}
                             }}/>
                    </Tabs>

                    {activeTab === "documents" && (
                        <Stack gap={2}>
                            <Stack direction={"row"} gap={1.5}>
                                <TextField
                                    fullWidth size={"small"}
                                    value={searchDocs}
                                    onChange={(e) => setSearchDocs(e.target.value)}
                                    placeholder={"Search documents..."}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position={"start"}>
                                                <HugeiconsIcon icon={Search01Icon} size={15}/>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "divider",
                                                borderWidth: "1px",
                                            },
                                        },
                                    }}
                                />
                                <Button
                                    variant={"modal"}
                                    startIcon={<HugeiconsIcon icon={Upload01Icon} size={16}/>}
                                    onClick={() => setUploadOpen(true)}
                                    sx={{
                                        bgcolor: "#000",
                                        color: "#fff",
                                        px: 2.5,
                                        py: 0.75,
                                        fontSize: 13,
                                        fontWeight: 500,
                                        textTransform: "none",
                                        borderRadius: 2,
                                        boxShadow: "none",
                                        whiteSpace: "nowrap",
                                        "&:hover": {bgcolor: "#000"},
                                    }}
                                >
                                    Upload Documents
                                </Button>
                            </Stack>

                            {filteredDocs.length === 0 ? (
                                <Card sx={{
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: "none",
                                    py: 6,
                                    textAlign: "center"
                                }}>
                                    <HugeiconsIcon icon={File02Icon} size={36} color={"#9ca3af"} style={{opacity: 0.6, marginBottom: 8}}/>
                                    <Typography fontSize={14}>No documents yet</Typography>
                                    <Typography fontSize={12} color={"text.secondary"} sx={{mt: 0.5}}>
                                        Upload documents to build the knowledge base for this topic
                                    </Typography>
                                    <Button
                                        variant={"modal"}
                                        startIcon={<HugeiconsIcon icon={Upload01Icon} size={16}/>}
                                        onClick={() => setUploadOpen(true)}
                                        sx={{
                                            mt: 2,
                                            textTransform: "none",
                                            borderColor: "divider",
                                            color: "text.primary"
                                        }}
                                    >
                                        Upload Documents
                                    </Button>
                                </Card>
                            ) : (
                                <Card sx={{
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: "none"
                                }}>
                                    {filteredDocs.map((doc, idx) => {
                                        const style = statusColor(doc.status);
                                        return (
                                            <Box key={doc.id}>
                                                {idx > 0 && <Divider/>}
                                                <Stack
                                                    direction={"row"} alignItems={"center"} gap={1.5}
                                                    onClick={() => {
                                                        setPreviewDoc(doc);
                                                        setPreviewOpen(true);
                                                    }}
                                                    sx={{
                                                        p: 1.75,
                                                        cursor: "pointer",
                                                        "&:hover": {bgcolor: "action.hover"},
                                                        "&:hover .doc-actions": {opacity: 1},
                                                    }}
                                                >
                                                    <Box sx={{
                                                        width: 36, height: 36, borderRadius: 1.5,
                                                        bgcolor: "action.hover", display: "flex",
                                                        alignItems: "center", justifyContent: "center", flexShrink: 0,
                                                    }}>
                                                        <HugeiconsIcon icon={File02Icon} size={16}/>
                                                    </Box>
                                                    <Box sx={{flex: 1, minWidth: 0}}>
                                                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                                                            <Typography fontSize={13} noWrap>{doc.name}</Typography>
                                                            <Chip
                                                                label={doc.status} size={"small"}
                                                                sx={{
                                                                    fontSize: 9,
                                                                    height: 18,
                                                                    bgcolor: style.bg,
                                                                    color: style.color
                                                                }}
                                                            />
                                                        </Stack>
                                                        <Stack direction={"row"} gap={1.5}
                                                               sx={{mt: 0.25, fontSize: 11, color: "text.secondary"}}>
                                                            <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                                <HugeiconsIcon icon={SdCardIcon} size={12}/> {formatSize(doc.size)}
                                                            </Stack>
                                                            {doc.chunks > 0 && (
                                                                <Stack direction={"row"} alignItems={"center"}
                                                                       gap={0.5}>
                                                                     <HugeiconsIcon icon={Layers01Icon} size={12}/> {doc.chunks} chunks
                                                                </Stack>
                                                            )}
                                                            <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                                 <HugeiconsIcon icon={Clock01Icon}
                                                                    size={12}/> {formatDate(doc.uploadedAt, true)}
                                                            </Stack>
                                                        </Stack>
                                                    </Box>
                                                    <Stack direction={"row"} className={"doc-actions"}
                                                           sx={{opacity: 0, transition: "opacity 0.15s"}}
                                                           onClick={(e) => e.stopPropagation()}>
                                                        <IconButton
                                                            size={"small"}
                                                            sx={{color: "error.main"}}
                                                            onClick={() => {
                                                                setDeleteDocId(doc.id);
                                                                setDeleteDialogOpen(true);
                                                            }}
                                                        >
                                                             <HugeiconsIcon icon={Delete02Icon} size={16}/>
                                                        </IconButton>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Card>
                            )}
                        </Stack>
                    )}

                    {activeTab === "query" && <QueryInterface topic={topic}/>}
                    {activeTab === "graph" && <KnowledgeGraphView topic={topic}/>}
                    {activeTab === "settings" &&
                        <ServerConfig topic={topic} onUpdated={() => refreshKbTopic(topic.id)}/>}

                </Box>
            </Box>

            <DocumentUpload
                topicId={topic.id}
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onUploaded={() => loadKbWorkspace(topicId)}
            />

            <DocumentPreview
                document={previewDoc}
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
            />

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{fontWeight: 700}}>
                    {deleteDocId ? "Delete Document" : "Delete Topic"}
                </DialogTitle>
                <DialogContent>
                    <Typography fontSize={13} color={"text.secondary"}>
                        {deleteDocId
                            ? "This will remove the document from the knowledge base. This action cannot be undone."
                            : "This will permanently delete this topic and all its documents. This action cannot be undone."}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button
                        variant="ghost"
                        onClick={() => {
                            setDeleteDocId(null);
                            setDeleteDialogOpen(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={"contained"}
                        color={"error"}
                        disabled={isDeleting}
                        onClick={deleteDocId ? handleDeleteDoc : handleDeleteTopic}
                        sx={{textTransform: "none", boxShadow: "none"}}
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

        </KnowledgeManagerLayout>
    );
};
